import React, { useCallback, useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";
import frLocale from "@fullcalendar/core/locales/fr";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Typography,
  message,
  Select,
  Row,
  Col,
  Card,
  Modal,
  Input,
  Button,
  DatePicker,
} from "antd";

const { Title } = Typography;
const { Option } = Select;

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function CalendarCreationUI() {
  const { token } = useToken();
  const navigate = useNavigate();

  // Data
  const [patients, setPatients] = useState([]);
  const [events, setEvents] = useState([]);

  // Selections
  const [selectedPatient, setSelectedPatient] = useState("");
  const [selectedProgram] = useState("");

  // Modal (create/edit)
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSessionName, setSelectedSessionName] = useState("");
  const [newSessionDate, setNewSessionDate] = useState(null);
  const [newSessionEndTime, setNewSessionEndTime] = useState(null);

  // Event info modal
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);


  // T0 specific
  const [evaluationDuration, setEvaluationDuration] = useState(60);
  const [t0Modality, setT0Modality] = useState("Présentiel");
  const isT0 = selectedSessionName?.toLowerCase().includes("évaluation t0");


  const formatDateToLocal = (date) => {
    return dayjs.utc(date).tz("America/Toronto").format("DD/MM/YYYY HH:mm");
  };
  const toUTCISOString = (localDate) => {
    return dayjs(localDate).tz("America/Toronto").utc().toISOString();
  };

  // --------- FETCH DROPDOWNS ---------
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const patientsRes = await axios.get(`${Constants.SERVER_URL}/patients`, {
          headers: { Authorization: "Bearer " + token },
        });
        setPatients(patientsRes.data);
      } catch (error) {
        console.error("Erreur chargement des données :", error);
      }
    };
    fetchDropdowns();
  }, [token]);

  // --------- FETCH SESSIONS (par patient) ---------
  const fetchCalendarSessions = useCallback(async () => {
    if (!selectedPatient) return;
    try {
      const res = await axios.get(
        `${Constants.SERVER_URL}/calendar-sessions/by-patient/${selectedPatient}`,
        { headers: { Authorization: "Bearer " + token } }
      );

      if (!Array.isArray(res.data)) {
        console.warn("La réponse reçue n'est pas une liste :", res.data);
        return;
      }

      const sessions = res.data.sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      );
      const sessionEvents = [];

      for (let i = 0; i < sessions.length; i++) {
        const current = sessions[i];
        const group = [current];

        // regrouper journées consécutives
        while (
          i + 1 < sessions.length &&
          sessions[i + 1].name === current.name &&
          sessions[i + 1].startDate.slice(11, 16) ===
            current.startDate.slice(11, 16) &&
          dayjs(sessions[i + 1].startDate).diff(
            dayjs(sessions[i].startDate),
            "day"
          ) === 1
        ) {
          group.push(sessions[++i]);
        }

        if (group.length > 1) {
          sessionEvents.push({
            id: group[0].id,
            title: group[0].name + ` (${group.length} jours)`,
            start: group[0].startDate,
            end: dayjs(group[group.length - 1].startDate)
              .hour(23)
              .minute(59)
              .second(59)
              .toISOString(),
            allDay: false,
            backgroundColor: group[0].color || undefined, // âœ… conserver la couleur
            extendedProps: {
              patientId: group[0].PatientId,
              programId: group[0].ProgramId,
              groupIds: group.map((s) => s.id),
              evaluationDuration: current.evaluationDuration,
            },
          });
        } else {
          sessionEvents.push({
            id: current.id,
            title: current.name,
            start: current.startDate,
            end: current.endDate,
            allDay: false,
            backgroundColor: current.color || undefined, // âœ…
            extendedProps: {
              patientId: current.PatientId,
              programId: current.ProgramId,
              evaluationDuration: current.evaluationDuration || "",
            },
          });
        }
      }

      setEvents(sessionEvents);
    } catch (error) {
      console.error("Erreur chargement rendez-vous calendrier :", error);
    }
  }, [selectedPatient, token]);

  // --------- MAJ heure de fin auto pour T0 ---------
  useEffect(() => {
    if (isT0 && newSessionDate && evaluationDuration) {
      const end = dayjs.utc(newSessionDate).add(evaluationDuration, "minute");
      setNewSessionEndTime(toUTCISOString(end));
    }
  }, [isT0, newSessionDate, evaluationDuration]);

  // --------- FETCH GLOBAL ---------
  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(
        `${Constants.SERVER_URL}/calendar-sessions/calendar-sessions`,
        { headers: { Authorization: "Bearer " + token } }
      );

      const sessions = response.data;

      const filteredSessions = sessions.filter((session) => {
  const name = session.name?.toLowerCase() || "";

  return (
    name.includes("appel de suivi") ||
    name.includes("prochaine évaluation") ||
    name.includes("évaluation") ||
    name.includes("enseignement du programme")
  );
});

      const formattedEvents = filteredSessions.map((session) => {
        const patientName = session.Patient
          ? `${session.Patient.firstname} ${session.Patient.lastname}`
          : "";
        return {
          id: session.id,
          title: `${session.name}${
            patientName ? " (" + patientName + ")" : ""
          }`,
          start: session.startDate,
          end: session.endDate,
          backgroundColor: session.color || "#00BCD4",
        };
      });

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Erreur lors du chargement des rendez-vous :", error);
      message.error("Impossible de charger les rendez-vous.");
    }
  }, [token]);

  // --------- SWITCH PATIENT / GLOBAL ---------
  useEffect(() => {
    if (selectedPatient) {
      fetchCalendarSessions();
    } else {
      fetchEvents();
    }
  }, [selectedPatient, fetchCalendarSessions, fetchEvents]);

  // --------- HANDLERS CALENDAR ---------
  const DEFAULT_T0_DURATION_MIN = 60;

  const handleDateClick = (info) => {
    setSelectedEvent(null);
    setSelectedSessionName("?valuation T0");
    setEvaluationDuration(DEFAULT_T0_DURATION_MIN);

    const start = dayjs(info.dateStr).hour(10).minute(0).second(0);
    setNewSessionDate(toUTCISOString(start));
    // fin T0 calculée par useEffect

    setIsModalVisible(true);
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setIsEventModalVisible(true);
  };

  const handleEventDrop = (info) => {
    const updatedEvents = events.map((event) =>
      event.id === info.event.id
        ? { ...event, start: info.event.startStr }
        : event
    );
    setEvents(updatedEvents);
    message.success("Session déplacée !");
  };

  const handleEventResize = async (info) => {
    const updatedEvent = info.event;
    const fullStart = updatedEvent.start.toISOString();
    const fullEnd = updatedEvent.end.toISOString();

    try {
      await axios.put(
        `${Constants.SERVER_URL}/calendar-sessions/${updatedEvent.id}`,
        { startDate: fullStart, endDate: fullEnd },
        { headers: { Authorization: "Bearer " + token } }
      );
      await fetchCalendarSessions();
      message.success("Rendez-vous modifié avec succès !");
    } catch (error) {
      console.error("Erreur lors de la modification du rendez-vous :", error);
      message.error("Erreur lors de la modification.");
    }
  };

  // --------- CRUD ---------
  const handleCreateSession = async () => {
    const isT0Local = selectedSessionName?.toLowerCase().includes("évaluation t0");

    // âœ… validation conditionnelle: pas besoin de programme pour T0
    if (
      !selectedSessionName ||
      !newSessionDate ||
      !selectedPatient ||
      (!isT0Local && (!newSessionEndTime || !selectedProgram))
    ) {
      return message.error("Veuillez remplir tous les champs.");
    }

    // âœ… calcule la fin si T0
    let endISO = newSessionEndTime;
    if (isT0Local) {
      endISO = dayjs
        .utc(newSessionDate)
        .add(evaluationDuration || 60, "minute")
        .toISOString();
      setNewSessionEndTime(endISO);
    }

    const payload = {
      name: selectedSessionName,
      startDate: newSessionDate,
      endDate: endISO,
      patientId: selectedPatient,
      programId: isT0Local ? null : selectedProgram,
      ...(isT0Local && {
        type: "evaluation",
        color: "#00C853",
        evaluationDuration,
        modality: t0Modality,
      }),
    };

    try {
      if (selectedEvent) {
        await axios.put(
          `${Constants.SERVER_URL}/calendar-sessions/${selectedEvent.id}`,
          payload,
          { headers: { Authorization: "Bearer " + token } }
        );
        await fetchCalendarSessions();
        message.success("Rendez-vous modifié !");
      } else {
        const res = await axios.post(
          `${Constants.SERVER_URL}/calendar-sessions`,
          payload,
          { headers: { Authorization: "Bearer " + token } }
        );
        // âœ… couleur immédiate au rendu
        setEvents((prev) => [
          ...prev,
          {
            id: res.data.id,
            title: res.data.name,
            start: res.data.startDate,
            end: res.data.endDate,
            allDay: false,
            backgroundColor:
              res.data.color || (isT0Local ? "#00C853" : undefined),
          },
        ]);
        message.success("Rendez-vous créé !");
      }

      setIsModalVisible(false);
      setSelectedSessionName("");
      setNewSessionDate(null);
      setNewSessionEndTime(null);
      setSelectedEvent(null);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde :", error);
      message.error("Erreur lors de la sauvegarde.");
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await axios.delete(
        `${Constants.SERVER_URL}/calendar-sessions/${selectedEvent.id}`,
        { headers: { Authorization: "Bearer " + token } }
      );

      message.success("Rendez-vous supprimé avec succès");
      setIsEventModalVisible(false);
      await fetchCalendarSessions();
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      message.error("?chec de la suppression");
    }
  };

  const handleUpdateSession = async () => {
    try {
      await axios.put(
        `${Constants.SERVER_URL}/calendar-sessions/${Number(
          selectedEvent.id
        )}`,
        {
          name: selectedSessionName,
          startDate: newSessionDate,
          endDate: newSessionEndTime,
        },
        { headers: { Authorization: "Bearer " + token } }
      );

      message.success("Séance mise à jour !");
      setIsModalVisible(false);
      await fetchCalendarSessions();
    } catch (error) {
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleEditEvent = () => {
    setSelectedSessionName(selectedEvent.title);
    const start = dayjs(selectedEvent.start);
    const end = dayjs(selectedEvent.end);

    setNewSessionDate(toUTCISOString(start));
    setNewSessionEndTime(toUTCISOString(end));

    setIsEventModalVisible(false);
    setIsModalVisible(true);
  };

  const handleDownloadPDF = async () => {
    const calendarElement = document.querySelector(".fc");
    const canvas = await html2canvas(calendarElement);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("landscape", "mm", "a4");
    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, width, height);
    pdf.save("calendrier.pdf");
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto", position: "relative" }}>
      <Title level={2} style={{ marginBottom: 32 }}>Calendrier des sessions</Title>

      <Card style={{ marginBottom: 24, backgroundColor: "#fafafa", borderRadius: 8 }}>
        <Row gutter={16}>
          <Col span={12}>
            
            <Select
              placeholder="recherche par patient"
              onChange={setSelectedPatient}
              value={selectedPatient || undefined}
              style={{ width: "100%" }}
            >
              {patients.map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.firstname} {p.lastname}
                </Option>
              ))}
            </Select>
          </Col>
          {/* Si tu veux remettre le choix de programme pour d'autres RDV :
          <Col span={12}>
            <label>Programme :</label>
            <Select
              placeholder="Choisir un programme"
              onChange={setSelectedProgram}
              value={selectedProgram || undefined}
              style={{ width: "100%" }}
              allowClear
            >
              {programs.map((prg) => (
                <Option key={prg.id} value={prg.id}>{prg.name}</Option>
              ))}
            </Select>
          </Col> */}
        </Row>
      </Card>

      <Card style={{ padding: 16, borderRadius: 8, backgroundColor: "#ffffff", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>
        <FullCalendar
          timeZone="America/Toronto"
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          editable={true}
          eventResize={handleEventResize}
          eventDrop={handleEventDrop}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          events={events}
          eventColor="#1890ff"
          headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,dayGridWeek,dayGridDay" }}
          locales={[frLocale]}
          locale="fr"
          firstDay={1}
          height={650}
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", meridiem: false, omitZeroMinute: false }}
          eventContent={(arg) => {
            const title = arg.event.title;

            const match = title.match(/^(.*)\((.*)\)$/);
            const mainTitle = match ? match[1].trim() : title;
            const patientName = match ? match[2].trim() : "";

            const isNextEval = mainTitle?.toLowerCase().startsWith("prochaine évaluation");
            const isEvalT0 = mainTitle?.toLowerCase().includes("évaluation t0");
            const isTeaching = mainTitle?.toLowerCase().includes("enseignement du programme");

            const color = isTeaching
              ? "#795548" // marron
              : isNextEval
              ? "red"
              : isEvalT0
              ? "#00C853"
              : "#1890ff";

            return {
              html: `
                <div style="
                  color: ${color};
                  font-size: 13px;
                  line-height: 1.2;
                  white-space: normal;
                  word-break: break-word;
                  overflow-wrap: break-word;
                  display: block;
                ">
                  ${mainTitle}<br/>
                  ${patientName ? `<span style="font-size: 10px;">(${patientName})</span>` : ""}
                </div>
              `,
            };
          }}
        />
        <Button onClick={handleDownloadPDF} style={{ marginTop: 16 }}>
          Télécharger en PDF
        </Button>
      </Card>

      {/* MODAL CREATE / EDIT */}
    <Modal
  title={`Créer un rendez-vous ${isT0 ? '(Evaluation T0)' : ''}`}
  open={isModalVisible}
  onCancel={() => setIsModalVisible(false)}
  footer={[
    selectedEvent
      ? <Button key="save" onClick={handleUpdateSession}>Enregistrer</Button>
      : <Button key="create" type="primary" onClick={handleCreateSession}>Créer</Button>
  ]}
>
  {/* Date de début */}
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontWeight: 500, marginBottom: 4 }}>Date  :</div>
    <DatePicker
      showTime
      value={newSessionDate ? dayjs.utc(newSessionDate).tz("America/Toronto") : null}
      onChange={(value) => setNewSessionDate(toUTCISOString(value))}
      style={{ width: "100%" }}
    />
  </div>

  {/* Si T0 ? Modalité + Durée ; sinon ? Date de fin */}
  {isT0 ? (
    <>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>Modalité :</div>
        <Select
          value={t0Modality}
          onChange={setT0Modality}
          style={{ width: "100%" }}
          options={[
            { value: "Présentiel", label: "Présentiel" },
            { value: "Téléphone", label: "Téléphone" },
            { value: "Visio", label: "Visio" },
          ]}
          placeholder="Modalité d'évaluation"
        />
      </div>

      <div>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>Durée (minutes) :</div>
        <Input
          type="number"
          min={15}
          max={180}
          value={evaluationDuration}
          onChange={(e) => setEvaluationDuration(Number(e.target.value || 60))}
          placeholder="Durée (minutes)"
        />
      </div>
    </>
  ) : (
    <div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>Date de fin :</div>
      <DatePicker
        showTime
        value={newSessionEndTime ? dayjs.utc(newSessionEndTime).tz("America/Toronto") : null}
        onChange={(value) => setNewSessionEndTime(toUTCISOString(value))}
        style={{ width: "100%" }}
      />
    </div>
  )}
</Modal>


      {/* MODAL DETAILS */}
      <Modal
        title="Détails du rendez-vous"
        open={isEventModalVisible}
        onCancel={() => setIsEventModalVisible(false)}
        footer={[
          <Button key="delete" danger onClick={handleDeleteEvent}>
            Supprimer
          </Button>,
          <Button key="edit" type="primary" onClick={handleEditEvent}>
            Modifier
          </Button>,
          ...(selectedEvent?.title?.toLowerCase().includes("appel de suivi") ? [
            <Button
              key="followup"
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
              onClick={() => {
                const pid = selectedEvent?.extendedProps?.patientId || selectedPatient;
                if (pid) {
                  const eventDate = selectedEvent?.start ? dayjs(selectedEvent.start).format("YYYY-MM-DD") : null;
                  setIsEventModalVisible(false);
                  navigate(`/follow-up-pace/${pid}`, { state: { eventDate } });
                } else {
                  message.warning("Veuillez sélectionner un patient.");
                }
              }}
            >
              Appel de suivi
            </Button>,
          ] : []),
        ]}
      >
        {selectedEvent && (
          <>
            <p>
              <strong>Nom :</strong> {selectedEvent.title}
            </p>
            <p>
              <strong>Date de début :</strong>{" "}
              {formatDateToLocal(selectedEvent?.start)}
            </p>
            <p>
              <strong>Date de fin :</strong>{" "}
              {formatDateToLocal(selectedEvent?.end)}
            </p>
          </>
        )}
      </Modal>

      <div style={{ position: "fixed", bottom: 32, right: 32, zIndex: 9999 }}></div>
    </div>
  );
}
